import { LocationScope } from "@swivl-mono/types";

import { RouterWorksheetData } from "../../Components/Worksheets/Types/Worksheet.Interface";
import AdminPage, { PAGE_KEYS as AdminKeys, SEARCH_LABELS as AdminSearchLabels } from "./Pages/Admin.Page";
import AmenitiesPage, { PAGE_KEYS as AmenitiesKeys, SEARCH_LABELS as AmenitiesSearchLabels } from "./Pages/Amenities.Page";
import CallScriptsPage, { PAGE_KEYS as CallScriptsKeys, SEARCH_LABELS as CallScriptsSearchLabels } from "./Pages/CallScripts.Page";
import ConversationStartersPage, { PAGE_KEYS as ConversationStartersKeys } from "./Pages/ConversationStarters.Page";
import DiscountsPage, { PAGE_KEYS as DiscountsKeys, SEARCH_LABELS as DiscountsSearchLabels } from "./Pages/Discounts.Page";
import EmergencyPage, { PAGE_KEYS as EmergencyKeys, SEARCH_LABELS as EmergencySearchLabels } from "./Pages/Emergency.Page";
import EscalationAndTicketingPage, {
  PAGE_KEYS as EscalationKeys,
  SEARCH_LABELS as EscalationSearchLabels
} from "./Pages/EscalationAndTicketing.Page";
import FacilityInfoPage, { PAGE_KEYS as FacilityInfoKeys, SEARCH_LABELS as FacilityInfoSearchLabels } from "./Pages/FacilityInfo.Page";
import GoogleReviewsPage, { PAGE_KEYS as GoogleReviewsKeys } from "./Pages/GoogleReviews.Page";
import HoursPage, { PAGE_KEYS as HoursKeys, SEARCH_LABELS as HoursSearchLabels } from "./Pages/Hours.Page";
import URLsPage, { PAGE_KEYS as URLsKeys, SEARCH_LABELS as URLsSearchLabels } from "./Pages/URLs.Page";
import WebsitePage, { PAGE_KEYS as WebsiteKeys } from "./Pages/Website.Page";
import WelcomeMessagesPage, { PAGE_KEYS as WelcomeMessagesKeys } from "./Pages/WelcomeMessages.Page";

/**
 * Details V3 — same shell as the legacy Details tab (header + left rail), but
 * powered by the new Cortex structured-var registries.
 *
 * See docs/snowy/plans/2026-05-01-details-v3.md.
 *
 * `searchLabels` feeds global search — a page's registration here is enough.
 */
const SHARED_PAGES: RouterWorksheetData[] = [
  { to: `facility-info`, label: "Facility Info", element: <FacilityInfoPage />, keys: FacilityInfoKeys, searchLabels: FacilityInfoSearchLabels },
  { to: `amenities`, label: "Amenities & Services", element: <AmenitiesPage />, keys: AmenitiesKeys, searchLabels: AmenitiesSearchLabels },
  { to: `discounts`, label: "Discounts", element: <DiscountsPage />, keys: DiscountsKeys, searchLabels: DiscountsSearchLabels },
  { to: `urls`, label: "URLs", element: <URLsPage />, keys: URLsKeys, searchLabels: URLsSearchLabels },
  { to: `google-reviews`, label: "Google Reviews", element: <GoogleReviewsPage />, keys: GoogleReviewsKeys }
  // Gate Code / Move-out / Profanity Handling settings render inside their
  // policy worksheets on the Policies tab (PolicyWorksheetsView) — they are
  // policy-topic settings, not facility details.
];

// Hours page renders at every scope — schedules and holiday rules cascade
// org → group → location, so each level needs its own editor. The page reads
// the active scope from the URL (group/location params) and adapts.
const HOURS_PAGE: RouterWorksheetData = { to: `hours`, label: "Hours", element: <HoursPage />, keys: HoursKeys, searchLabels: HoursSearchLabels };
const EMERGENCY_PAGE: RouterWorksheetData = {
  to: `emergency`,
  label: "Emergency",
  element: <EmergencyPage />,
  keys: EmergencyKeys,
  searchLabels: EmergencySearchLabels
};
const ESCALATION_PAGE: RouterWorksheetData = {
  to: `escalation-and-ticketing`,
  label: "Escalation & Ticketing",
  element: <EscalationAndTicketingPage />,
  keys: EscalationKeys,
  searchLabels: EscalationSearchLabels
};
// ENG-3356 — operator-named call scripts. Its own page, right after Escalation & Ticketing.
// Swivl staff only until the operator release (Chuck, 2026-10-01, ENG-3475).
const CALL_SCRIPTS_PAGE: RouterWorksheetData = {
  to: `call-scripts`,
  label: "Call Scripts",
  element: <CallScriptsPage />,
  keys: CallScriptsKeys,
  swivlStaffOnly: true,
  searchLabels: CallScriptsSearchLabels
};
// Tiered welcome messages (ENG-3115) are per-org by spec — no group/location variants —
// so the page is offered at org scope only.
const WELCOME_MESSAGES_PAGE: RouterWorksheetData = {
  to: `welcome-messages`,
  label: "Welcome Messages",
  element: <WelcomeMessagesPage />,
  keys: WelcomeMessagesKeys
};
// Website provider ids and spot slugs are provisioned by Swivl, not by the operator — same as
// their legacy counterparts on the org admin subpage (ENG-3138).
const WEBSITE_PAGE: RouterWorksheetData = {
  to: `website`,
  label: "Website",
  element: <WebsitePage />,
  keys: WebsiteKeys,
  swivlStaffOnly: true
};
// How each Conversation Starter opens — Automatic or the operator's exact words — cascading
// org → group → location, so it renders at every scope (Chuck, 2026-08-30).
const CONVERSATION_STARTERS_PAGE: RouterWorksheetData = {
  to: `conversation-starters`,
  label: "Conversation Starters",
  element: <ConversationStartersPage />,
  keys: ConversationStartersKeys
};
const ADMIN_PAGE: RouterWorksheetData = {
  to: `admin`,
  label: "Admin",
  element: <AdminPage />,
  keys: AdminKeys,
  swivlStaffOnly: true,
  searchLabels: AdminSearchLabels
};

export const DetailsV3WorksheetsData: { [key in LocationScope]: RouterWorksheetData[] } = {
  [LocationScope.ORG]: [
    ...SHARED_PAGES,
    HOURS_PAGE,
    EMERGENCY_PAGE,
    ESCALATION_PAGE,
    CALL_SCRIPTS_PAGE,
    WELCOME_MESSAGES_PAGE,
    CONVERSATION_STARTERS_PAGE,
    WEBSITE_PAGE,
    ADMIN_PAGE
  ],
  [LocationScope.GROUP]: [
    ...SHARED_PAGES,
    HOURS_PAGE,
    EMERGENCY_PAGE,
    ESCALATION_PAGE,
    CALL_SCRIPTS_PAGE,
    CONVERSATION_STARTERS_PAGE,
    WEBSITE_PAGE,
    ADMIN_PAGE
  ],
  [LocationScope.LOCATION]: [
    ...SHARED_PAGES,
    HOURS_PAGE,
    EMERGENCY_PAGE,
    ESCALATION_PAGE,
    CALL_SCRIPTS_PAGE,
    CONVERSATION_STARTERS_PAGE,
    WEBSITE_PAGE,
    ADMIN_PAGE
  ]
};
